import {
	Box,
	Button,
	ButtonGroup,
	Card,
	CardHeader,
	Chip,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
} from "@mui/material";
import {
	BreadcrumbItem,
	BreadcrumbLink,
	Breadcrumbs,
	View,
	ViewHeader,
} from "@variocube/app-ui";
import React from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {Link, useNavigate} from "react-router-dom";
import {User, useUserApi} from "../../api";
import {LabeledText} from "../../component/labeled-text";
import {hasRoles, useAuthContext} from "../../context/auth";
import {useLocalization} from "../../i18n";
import {useNumericParam} from "../../utils/useParam";
import {getUserDisplayName} from "../../utils/user";
import {InboxTable} from "./inbox-table";

export function UserView() {
	const navigate = useNavigate();
	const {t} = useLocalization();
	const {getUser, queryUserInboxes, queryUserWorksAt, queryUserProxies, queryProxiedUser, impersonateUser, updateUserInboxes, deleteUserInbox} =
		useUserApi();
	const {user} = useAuthContext();
	const {reloadAuth} = useAuthContext();
	const id = useNumericParam("id");

	const {loading, error, result} = useAsync(getUser, [id]);
	const {result: inboxes, execute: refreshInboxes} = useAsync(queryUserInboxes, [id]);
	const {result: worksAt} = useAsync(queryUserWorksAt, [id]);
	const {result: proxies} = useAsync(queryUserProxies, [id]);
	const {result: proxied} = useAsync(queryProxiedUser, [id]);

	const impersonate = useAsyncCallback(async () => {
		await impersonateUser(id);
		await reloadAuth();
		navigate("/");
	});

	const canManageInboxes = hasRoles(user, ["SuperUser", "OebbAdministrator"]);

	async function handleAddInboxes(ids: number[]) {
		if (inboxes) {
			await updateUserInboxes(id, [...inboxes.map(i => i.id), ...ids]);
			await refreshInboxes(id);
		}
	}

	async function handleRemoveInbox(inboxId: number) {
		await deleteUserInbox(id, inboxId);
		await refreshInboxes(id);
	}

	const canEdit = hasRoles(user, ["SuperUser", "CreditorAdministrator"]);

	return (
		<View loading={loading} error={error}>
			<Breadcrumbs>
				<BreadcrumbItem>{t("navigation.admin")}</BreadcrumbItem>
				<BreadcrumbLink component={Link} to={"/users"}>{t("users.plural")}</BreadcrumbLink>
				<BreadcrumbItem>{getUserDisplayName(result)}</BreadcrumbItem>
			</Breadcrumbs>
			<ViewHeader
				title={getUserDisplayName(result)}
				actions={
					<ButtonGroup>
						{hasRoles(user, ["SuperUser"]) && (
							<Button
								variant="outlined"
								color="inherit"
								onClick={impersonate.execute}
								disabled={impersonate.loading}
							>
								In Vertretung einloggen
							</Button>
						)}
						{canEdit && (
							<Button
								variant="outlined"
								color="primary"
								component={Link}
								to={`/users/${id}/edit`}
							>
								{t("common.edit")}
							</Button>
						)}
					</ButtonGroup>
				}
			/>
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6}>
						<Card sx={{height: "100%"}}>
							<CardHeader title={t("users.contactData")} />
							<Box sx={{p: 2}}>
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<LabeledText label={t("common.firstName")} text={result?.firstname} />
									</Grid>
									<Grid item xs={6}>
										<LabeledText label={t("common.lastName")} text={result?.lastname} />
									</Grid>
									<Grid item xs={6}>
										<LabeledText label={t("common.email")} text={result?.email} />
									</Grid>
									<Grid item xs={6}>
										<LabeledText label={t("common.phone")} text={result?.phone} />
									</Grid>
								</Grid>
							</Box>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Card sx={{height: "100%"}}>
							<CardHeader title={t("users.role")} />
							<Box sx={{p: 2}}>
								<Grid container spacing={2}>
									<Grid item xs={6}>
										<LabeledText
											label={t("users.role")}
											text={result?.role ? t(`users.roles.${result.role}`) : ""}
										/>
									</Grid>
									<Grid item xs={6}>
										<LabeledText
											label={t("users.manageAuditors")}
											text={
												<Chip
													color={result?.canManageAuditors ? "success" : "default"}
													label={result?.canManageAuditors ? t("common.yes") : t("common.no")}
												/>
											}
										/>
									</Grid>
									<Grid item xs={6}>
										<LabeledText
											label={t("common.active")}
											text={
												<Chip
													color={result?.locked ? "error" : "success"}
													label={result?.locked ? t("common.no") : t("common.yes")}
												/>
											}
										/>
									</Grid>
								</Grid>
							</Box>
						</Card>
					</Grid>
					{["OebbAdministrator", "OebbUser"].includes(result?.role ?? "") && (
						<>
							<Grid item xs={12} md={6}>
								{canManageInboxes ? (
									<InboxTable
										title={t("users.mailboxes.inboxes.title")}
										inboxes={inboxes ?? []}
										filter="MANAGEMENT_ASSIGNMENT"
										onAdd={handleAddInboxes}
										onRemove={handleRemoveInbox}
										assignLabel={t("users.mailboxes.inboxes.assign")}
									/>
								) : (
									<ReadOnlyInboxTable
										title={t("users.mailboxes.inboxes.title")}
										inboxes={inboxes ?? []}
									/>
								)}
							</Grid>
							<Grid item xs={12} md={6}>
								<ReadOnlyInboxTable
									title={t("users.mailboxes.worksAt.title")}
									inboxes={worksAt ?? []}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<ReadOnlyProxyTable
									title={t("users.proxies.proxiedBy")}
									subheader={t("users.proxies.proxiedByHint")}
									users={proxied ?? []}
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<ReadOnlyProxyTable
									title={t("users.proxies.title")}
									subheader={t("users.proxies.hint")}
									users={proxies ?? []}
								/>
							</Grid>
						</>
					)}
				</Grid>
			</Box>
		</View>
	);
}

function ReadOnlyInboxTable({title, inboxes}: { title: string; inboxes: { id: number; name: string }[] }) {
	const {t} = useLocalization();
	return (
		<Card>
			<CardHeader title={title} />
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{t("common.name")}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{inboxes.map(inbox => (
						<TableRow key={inbox.id}>
							<TableCell>{inbox.name}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Card>
	);
}

function ReadOnlyProxyTable({title, subheader, users}: { title: string; subheader: string; users: User[] }) {
	const {t} = useLocalization();
	return (
		<Card>
			<CardHeader title={title} subheader={subheader} />
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell>{t("common.name")}</TableCell>
						<TableCell>{t("common.email")}</TableCell>
						<TableCell>{t("common.phone")}</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{users.map(u => (
						<TableRow key={u.id}>
							<TableCell>{u.lastname}, {u.firstname}</TableCell>
							<TableCell>{u.email}</TableCell>
							<TableCell>{u.phone}</TableCell>
						</TableRow>
					))}
				</TableBody>
			</Table>
		</Card>
	);
}
